(() => {
  window.AW = window.AW || {};

  class AWMainProductTimesact {
    constructor({ root, getVariantState, onStateChange, isElementVisible }) {
      this.root = root;
      this.getVariantState = getVariantState;
      this.onStateChange = onStateChange;
      this.isElementVisible = isElementVisible;
      this.observer = null;
      this.mutationFrame = null;
      this.timesactBisButton = null;
      this.timesactVariantSyncTimerId = null;
      this.timesactVariantChangeStartedAt = 0;
      this.onMutate = this.onMutate.bind(this);

      this.observeButtonsBlock();
    }

    refresh() {
      this.observeButtonsBlock();
      this.syncButtonPlacement();
      this.syncActionState();
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.mutationFrame !== null) {
        window.cancelAnimationFrame(this.mutationFrame);
        this.mutationFrame = null;
      }

      if (this.timesactVariantSyncTimerId !== null) {
        window.clearTimeout(this.timesactVariantSyncTimerId);
        this.timesactVariantSyncTimerId = null;
      }

      this.timesactVariantChangeStartedAt = 0;
      this.timesactBisButton = null;
    }

    onVariantChange(state, target) {
      if (!this.isVariantChangeTarget(target)) {
        return;
      }

      this.latestVariantState = state;
      this.timesactVariantChangeStartedAt = Date.now();
      this.scheduleVariantSync();
    }

    getVisibleButton() {
      const currentButton = this.root.querySelector(".timesact-button-bis");

      if (currentButton instanceof HTMLButtonElement) {
        this.timesactBisButton = currentButton;
      }

      return this.timesactBisButton instanceof HTMLButtonElement
        && this.isElementVisible(this.timesactBisButton)
        ? this.timesactBisButton
        : null;
    }

    isBisButton(element) {
      return element instanceof HTMLElement && element.classList.contains("timesact-button-bis");
    }

    hideSellingPlanDescriptions() {
      this.root.querySelectorAll(".timesact-selling-plan-description").forEach((node) => {
        if (node instanceof HTMLElement) {
          node.hidden = true;
          node.setAttribute("aria-hidden", "true");
        }
      });
    }

    observeButtonsBlock() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      const buttonsBlock = this.root.querySelector(".aw-main-product__block--buy-buttons");

      if (!(buttonsBlock instanceof HTMLElement)) {
        return;
      }

      this.observer = new MutationObserver(this.onMutate);
      this.observer.observe(buttonsBlock, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["style", "class", "hidden", "disabled"]
      });
      this.observer.observe(this.root, {
        childList: true,
        subtree: true
      });
    }

    captureBisButton(mutations = []) {
      let captured = false;

      const captureButton = (node) => {
        if (!(node instanceof Element)) {
          return;
        }

        const buttons = [];

        if (node.matches(".timesact-button-bis")) {
          buttons.push(node);
        }

        buttons.push(...node.querySelectorAll(".timesact-button-bis"));

        buttons.forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            this.timesactBisButton = button;
            captured = true;
          }
        });
      };

      mutations.forEach(({ addedNodes, removedNodes }) => {
        addedNodes.forEach(captureButton);
        removedNodes.forEach(captureButton);
      });

      return captured;
    }

    onMutate(mutations = []) {
      const capturedBisButton = this.captureBisButton(mutations);

      if (this.shouldResyncAfterButtonMutation(mutations)) {
        this.timesactVariantChangeStartedAt = 0;
        this.scheduleVariantSync(60);
      }

      if (this.shouldIgnoreMutations(mutations) && !capturedBisButton) {
        return;
      }

      if (this.mutationFrame !== null) {
        window.cancelAnimationFrame(this.mutationFrame);
      }

      this.mutationFrame = window.requestAnimationFrame(() => {
        this.mutationFrame = null;
        this.onStateChange();
      });
    }

    scheduleVariantSync(delay = 180) {
      if (this.timesactVariantSyncTimerId !== null) {
        window.clearTimeout(this.timesactVariantSyncTimerId);
      }

      this.timesactVariantSyncTimerId = window.setTimeout(() => {
        this.timesactVariantSyncTimerId = null;

        const state = this.getVariantState();
        const variantId = state?.variantId || "";

        if (!variantId || !window.ta?.currentTimesactProductData) {
          return;
        }

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("variant", variantId);
        window.history.replaceState(window.history.state, "", currentUrl.href);
      }, delay);
    }

    isVariantChangeTarget(target) {
      if (!(target instanceof Element)) {
        return false;
      }

      if (
        target.matches(
          "input[name='id'], select[name='id'], input[name='id[]'], select[name='id[]'], input[name^='options['], select[name^='options['], [data-popout-input]"
        )
      ) {
        return true;
      }

      return !!target.closest(".selector-wrapper, .form__selectors");
    }

    shouldResyncAfterButtonMutation(mutations) {
      if (this.timesactVariantChangeStartedAt === 0 || Date.now() - this.timesactVariantChangeStartedAt > 1500) {
        return false;
      }

      return mutations.some(({ addedNodes }) => Array.from(addedNodes).some((node) => {
        if (!(node instanceof Element)) {
          return false;
        }

        return node.matches("[data-add-to-cart], button[name='add']")
          || node.querySelector("[data-add-to-cart], button[name='add']") !== null;
      }));
    }

    shouldIgnoreMutations(mutations) {
      return mutations.length > 0 && mutations.every(({ target }) => {
        const element = target instanceof Element ? target : target?.parentElement;

        return element instanceof Element
          && !!element.closest(".gpo-container, .gpo-app, .gpo-element");
      });
    }

    syncButtonPlacement() {
      const currentButton = this.root.querySelector(".timesact-button-bis");

      if (currentButton instanceof HTMLButtonElement) {
        this.timesactBisButton = currentButton;
      }

      if (!(this.timesactBisButton instanceof HTMLButtonElement)) {
        return;
      }

      const buttonsContainer = this.root.querySelector(".product__submit__buttons");

      if (!(buttonsContainer instanceof HTMLElement)) {
        return;
      }

      const nativeButton =
        buttonsContainer.querySelector(".aw-main-product__add-button")
        || buttonsContainer.querySelector(".gpo-clone-atc-button")
        || buttonsContainer.querySelector(".gpo-original-atc-button")
        || buttonsContainer.querySelector("[data-add-to-cart]")
        || buttonsContainer.querySelector("button[name='add']");

      if (!(nativeButton instanceof HTMLElement)) {
        return;
      }

      const isCorrectParent = this.timesactBisButton.parentElement === buttonsContainer;
      const isCorrectPosition = this.timesactBisButton.previousElementSibling === nativeButton;

      if (!isCorrectParent || !isCorrectPosition) {
        nativeButton.insertAdjacentElement("afterend", this.timesactBisButton);
      }
    }

    syncActionState() {
      const isBisActive =
        this.timesactBisButton instanceof HTMLButtonElement
        && this.timesactBisButton.isConnected
        && this.isElementVisible(this.timesactBisButton);

      this.root.classList.toggle("aw-main-product--timesact-bis-active", isBisActive);
    }
  }

  window.AW.MainProductTimesact = AWMainProductTimesact;
})();
